import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';

import PlayerOddsTable from './PlayerOddsTable';
import { BOOKMAKERS_LIST, MLB_MARKETS, BOOKMAKER_ALIASES } from '../utils/constants';
import { useBooks } from '../contexts/BooksContext';
import AddToScratchpadLink from './AddToScratchpadLink';
import ModalBrandingBar from './ModalBrandingBar';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const PlayerModal = ({ show, onHide, gameId, playerId, playerName, market, point, awayTeam, homeTeam, getTeamLogoUrl, getBookLogoUrl, sport, game, opposingPitcher, bookmakers = [], recalcEdgePlayer = false }) => {
  const { isBookEnabledForGraphs } = useBooks();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showUnders, setShowUnders] = useState(false);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [oddsData, setOddsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [mlbPlayerData, setMlbPlayerData] = useState(null);
  const [showChart, setShowChart] = useState(false);
  const [chartMarket, setChartMarket] = useState(null);
  const [chartPoint, setChartPoint] = useState(null);
  const [chartIsOver, setChartIsOver] = useState(true); // Track if showing over or under
  const [oddsHistoryData, setOddsHistoryData] = useState(null);
  const [loadingChart, setLoadingChart] = useState(false);
  const [playerHandInfo, setPlayerHandInfo] = useState(null);

  // Stable callback for PlayerOddsTable hand info
  const handlePlayerInfo = useCallback((info) => {
    setPlayerHandInfo(info);
  }, []);

  // Tooltip state for edge %
  const [tooltipData, setTooltipData] = useState(null);

  // Helper function to get market abbreviation (same as main odds screen)
  const getMarketAbbreviation = (marketKey) => {
    if (!marketKey) return 'UNK';

    const marketMap = {
      'batter_hits': 'H',
      'batter_singles': '1B',
      'batter_rbis': 'RBI',
      'batter_runs_scored': 'R',
      'batter_hits_runs_rbis': 'H+R+R',
      'batter_home_runs': 'HR',
      'batter_doubles': '2B',
      'batter_triples': '3B',
      'batter_total_bases': 'TB',
      'batter_stolen_bases': 'SB',
      'pitcher_strikeouts': 'K',
      'pitcher_hits_allowed': 'Hits',
      'pitcher_outs': 'Outs'
    };
    return marketMap[marketKey] || marketKey.toUpperCase();
  };

  // Helper function to get book logo URL
  const getBookLogo = (bookName) => {
    const bookKey = bookName.toLowerCase();
    // Using the same pattern as other components
    return `/logos/${bookKey}.png`;
  };

  // Helper function to get tier coloring (same as main odds screen).
  // Uses inline recompute against best-vs-second-best (matching NFL/NHL/
  // NBA modals). When recalcEdgePlayer is on, the compare set is scoped
  // to books the user has enabled so tiers reflect only visible cells.
  const getTierColorForMarket = (market, bookName, odds) => {
    if (!market.books || !odds) {
      return { backgroundColor: 'transparent', color: 'white' };
    }

    // Collect valid odds values, optionally scoped to enabled bookmakers.
    let allOdds;
    if (recalcEdgePlayer && bookmakers.length > 0) {
      allOdds = bookmakers
        .map(b => market.books[b])
        .filter(v => v !== null && v !== undefined);
    } else {
      allOdds = Object.values(market.books).filter(v => v !== null && v !== undefined);
    }

    if (allOdds.length < 2) {
      return { backgroundColor: 'transparent', color: 'white' };
    }

    const bestOdds = Math.max(...allOdds);
    const bestCount = allOdds.filter(v => v === bestOdds).length;
    const isBest = odds === bestOdds && bestCount === 1;

    const style = {
      backgroundColor: 'transparent',
      color: 'white',
      fontWeight: 'bold'
    };

    if (!isBest) return style;

    // Diff = (best − second-best) / |second-best|, with cross-zero handling
    // matching NFL/NHL/NBA formula.
    const secondBestOdds = Math.max(...allOdds.filter(o => o !== bestOdds));
    let diff;
    if (bestOdds > 0 && secondBestOdds < 0) {
      const spreadPositive = bestOdds - 100;
      const spreadNegative = secondBestOdds - (-100);
      const totalSpread = spreadPositive - spreadNegative;
      diff = Math.abs(totalSpread / secondBestOdds);
    } else {
      diff = Math.abs((bestOdds - secondBestOdds) / Math.abs(secondBestOdds));
    }

    if (diff >= 0.25) {
      Object.assign(style, { backgroundColor: 'green', color: 'yellow' });
    } else if (diff >= 0.15) {
      Object.assign(style, { backgroundColor: '#F59E0B', color: '#1E40AF' });
    } else if (diff >= 0.05) {
      Object.assign(style, { backgroundColor: '#3B82F6', color: 'white' });
    } else {
      Object.assign(style, { color: '#F59E0B' });
    }
    return style;
  };

  // Process MLB player odds data into table format
  const processMLBOddsData = () => {
    if (!mlbPlayerData) return null;

    const marketMap = new Map(); // Use Map to deduplicate by market+tier key
    const allBooks = new Set();

    // Get available markets from the response
    const availableMarkets = mlbPlayerData.availableMarkets || [];

    availableMarkets.forEach(marketKey => {
      const marketData = mlbPlayerData[marketKey];
      if (marketData && typeof marketData === 'object') {
        // Process both over_under and alternates, but merge duplicates
        ['over_under', 'alternates'].forEach(oddsType => {
          if (marketData[oddsType] && Array.isArray(marketData[oddsType])) {
            marketData[oddsType].forEach(tierData => {
              if (tierData.books && tierData.point !== undefined) {
                const marketTierKey = `${marketKey}_${tierData.point}`;

                // Check if we already have this market+tier combination
                let tierMarketInfo = marketMap.get(marketTierKey);
                if (!tierMarketInfo) {
                  tierMarketInfo = {
                    key: marketTierKey,
                    displayName: `${getMarketAbbreviation(marketKey)} O${tierData.point}`,
                    books: {}
                  };
                  marketMap.set(marketTierKey, tierMarketInfo);
                }

                // Merge books for this tier, preferring over_under values over alternates
                Object.keys(tierData.books).forEach(bookKey => {
                  const bookData = tierData.books[bookKey];
                  if (bookData && typeof bookData === 'object' && bookData.over !== undefined) {
                    allBooks.add(bookKey);
                    // Only overwrite if we don't already have this book or if this is from over_under
                    if (!tierMarketInfo.books[bookKey] || oddsType === 'over_under') {
                      tierMarketInfo.books[bookKey] = bookData.over;
                    }
                    // Track isAvailable
                    if (bookData.over_isAvailable !== undefined) {
                      if (!tierMarketInfo.booksAvailable) tierMarketInfo.booksAvailable = {};
                      tierMarketInfo.booksAvailable[bookKey] = bookData.over_isAvailable;
                    }
                  }
                });

                // Also process under lines
                const underMarketTierKey = `${marketKey}_under_${tierData.point}`;
                let underTierMarketInfo = marketMap.get(underMarketTierKey);
                if (!underTierMarketInfo) {
                  underTierMarketInfo = {
                    key: underMarketTierKey,
                    displayName: `${getMarketAbbreviation(marketKey)} U${tierData.point}`,
                    books: {}
                  };
                  marketMap.set(underMarketTierKey, underTierMarketInfo);
                }

                Object.keys(tierData.books).forEach(bookKey => {
                  const bookData = tierData.books[bookKey];
                  if (bookData && typeof bookData === 'object' && bookData.under !== undefined) {
                    allBooks.add(bookKey);
                    // Only overwrite if we don't already have this book or if this is from over_under
                    if (!underTierMarketInfo.books[bookKey] || oddsType === 'over_under') {
                      underTierMarketInfo.books[bookKey] = bookData.under;
                    }
                    // Track isAvailable
                    if (bookData.under_isAvailable !== undefined) {
                      if (!underTierMarketInfo.booksAvailable) underTierMarketInfo.booksAvailable = {};
                      underTierMarketInfo.booksAvailable[bookKey] = bookData.under_isAvailable;
                    }
                  }
                });
              }
            });
          }
        });
      }
    });

    // Convert Map values to array and sort by market name, then by tier value
    const markets = Array.from(marketMap.values()).sort((a, b) => {
      const aMarket = a.key.split('_').slice(0, -1).join('_');
      const bMarket = b.key.split('_').slice(0, -1).join('_');
      const aTier = parseFloat(a.key.split('_').pop());
      const bTier = parseFloat(b.key.split('_').pop());

      if (aMarket === bMarket) {
        return aTier - bTier; // Sort tiers numerically
      }
      return aMarket.localeCompare(bMarket); // Sort markets alphabetically
    });

    // Always show all bookmakers, even if they have no odds
    return { markets, books: BOOKMAKERS_LIST };
  };

  // Color palette for different bookmakers - matching their brand colors
  const bookmakerColors = {
    fanduel: '#1A3D6C',      // FanDuel blue
    draftkings: '#5DD335',    // DraftKings green
    betmgm: '#FFCC00',        // BetMGM yellow/gold
    fanatics: '#002F6C',      // Fanatics navy blue
    espnbet: '#FF0033',       // ESPN red
    betrivers: '#EC1C24',     // BetRivers red
    ballybet: '#000000',      // Bally black
    bovada: '#CC0000',        // Bovada red
    caesars: '#000000',       // Caesars black (with secondary gold)
    hardrockbet: '#D4145A'    // Hard Rock pink/magenta
  };

  useEffect(() => {
    if (sport === 'mlb') {
      // For MLB on mobile, PlayerOddsTable handles data fetching — skip duplicate call
      // Desktop modal still needs fetchOddsMovement for its own table
      if (show && gameId && playerId && !isMobile) {
        fetchOddsMovement();
      } else if (!show) {
        // Reset state when modal closes
        setOddsData(null);
        setMlbPlayerData(null);
        setError(null);
        setSelectedTier(null);
        setPlayerHandInfo(null);

        setShowChart(false);
        setChartMarket(null);
        setChartPoint(null);
        setChartIsOver(true);
        setOddsHistoryData(null);
      }
    } else {
      // Original logic for other sports
      if (show && gameId && playerId && market) {
        fetchOddsMovement();
      } else if (!show) {
        // Reset state when modal closes
        setOddsData(null);
        setMlbPlayerData(null);
        setError(null);
        setSelectedTier(null);
        setPlayerHandInfo(null);

        setShowChart(false);
        setChartMarket(null);
        setChartPoint(null);
        setChartIsOver(true);
        setOddsHistoryData(null);
      }
    }
  }, [show, gameId, playerId, market, sport, isMobile]);

  // Get available tiers from the odds data
  const getAvailableTiers = () => {
    if (!oddsData || !oddsData.oddsHistory) return [];
    
    const tierSet = new Set();
    oddsData.oddsHistory.forEach(entry => {
      if (entry.point !== undefined && entry.point !== null) {
        tierSet.add(entry.point);
      }
    });
    
    return Array.from(tierSet).sort((a, b) => a - b);
  };

  // Auto-select tier when data loads - prefer the point passed from main screen
  useEffect(() => {
    if (oddsData && !selectedTier) {
      const tiers = getAvailableTiers();
      if (tiers.length > 0) {
        // If a point was passed from the main screen and it exists in available tiers, use it
        if (point !== null && point !== undefined && tiers.includes(point)) {
          setSelectedTier(point);
        } else {
          // Otherwise default to first tier
          setSelectedTier(tiers[0]);
        }
      }
    }
  }, [oddsData, selectedTier, point]);

  // Fetch odds history for a specific market and tier
  const fetchOddsHistory = async (marketKey, point = null, isOver = true) => {
    if (!gameId || !playerId || !marketKey) {
      console.error('Missing required parameters for odds history');
      return;
    }

    setLoadingChart(true);
    setError(null);
    try {
      const response = await fetch(`/api/games/${sport}/${gameId}/${playerId}/${marketKey}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setOddsHistoryData(data);
      setSelectedTier(point);
      setShowChart(true);
      setChartMarket(marketKey);
      setChartPoint(point);
      setChartIsOver(isOver); // Track whether we're showing over or under
    } catch (err) {
      console.error('Error fetching odds history:', err);
      setError(err.message);
    } finally {
      setLoadingChart(false);
    }
  };

  const handleBackToTable = () => {
    setShowChart(false);
    setChartMarket(null);
    setChartPoint(null);
    setChartIsOver(true);
    setOddsHistoryData(null);
    setSelectedTier(null);
  };

  // Calculate implied probability from American odds
  const calculateImpliedProbability = (odds) => {
    if (!odds || odds === '-') return null;
    const numericOdds = parseInt(odds);
    if (isNaN(numericOdds)) return null;

    if (numericOdds > 0) {
      // Positive odds: 100 / (odds + 100)
      return (100 / (numericOdds + 100)) * 100;
    } else {
      // Negative odds: |odds| / (|odds| + 100)
      return (Math.abs(numericOdds) / (Math.abs(numericOdds) + 100)) * 100;
    }
  };

  // Handle odds cell click to show tooltip
  const handleOddsClick = (event, odds, book, point, bestBook, bestBookDiff) => {
    if (!odds || odds === '-') return;

    const impliedProb = calculateImpliedProbability(odds);
    if (!impliedProb) return;

    // Edge is bestBookDiff * 100 only for the best book, 0 otherwise
    const edge = (book === bestBook && bestBookDiff) ? bestBookDiff * 100 : 0;

    // On mobile, center the tooltip; on desktop, position near cursor
    let x, y;
    if (isMobile) {
      // Center horizontally, position vertically based on click
      x = window.innerWidth / 2 - 100; // Assuming tooltip width ~200px
      y = Math.min(event.clientY, window.innerHeight - 150); // Keep above bottom
    } else {
      x = event.clientX;
      y = event.clientY;
    }

    setTooltipData({
      odds,
      book,
      point,
      impliedProb: impliedProb.toFixed(2),
      edge: edge.toFixed(2),
      x,
      y
    });
  };

  // Close tooltip when clicking outside (mobile only)
  useEffect(() => {
    if (isMobile) {
      const handleClickOutside = () => setTooltipData(null);
      if (tooltipData) {
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
      }
    }
  }, [tooltipData, isMobile]);

  const fetchOddsMovement = async () => {
    // For MLB, we don't need market parameter for the new endpoint
    if (sport === 'mlb') {
      if (!gameId || !playerId) {
        console.error('Missing required parameters:', { gameId, playerId });
        setError('Missing required parameters');
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/games/mlb/${gameId}/${playerId}`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        setMlbPlayerData(data);
        setOddsData(data); // Keep for compatibility
      } catch (err) {
        console.error('Error fetching MLB player data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    } else {
      // Original logic for other sports
      if (!gameId || !playerId || !market) {
        console.error('Missing required parameters:', { gameId, playerId, market });
        setError('Missing required parameters');
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/games/${gameId}/${playerId}/${market}`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        setOddsData(data);
      } catch (err) {
        console.error('Error fetching odds movement:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const prepareChartData = () => {
    // Use oddsHistoryData for MLB charts, oddsData for other sports
    const dataSource = showChart && oddsHistoryData ? oddsHistoryData : oddsData;

    if (!dataSource || !dataSource.oddsHistory || dataSource.oddsHistory.length === 0) {
      return null;
    }

    // Filter by market key and point value
    // For special markets (binary yes/no), selectedTier will be null
    // For over/under markets, filter by the selected point value
    const filteredHistory = dataSource.oddsHistory.filter(entry => {
      // First, filter by market key if chartMarket is set (API uses "marketType" field)
      if (chartMarket && entry.marketType !== chartMarket) return false;

      // Then filter by point value if applicable
      if (selectedTier !== null) {
        return entry.point === selectedTier;
      }

      return true;
    });

    // Get the over/under preference
    const useOver = showChart ? chartIsOver : true;

    if (filteredHistory.length === 0) {
      return null;
    }

    // Get all unique bookmakers from the filtered data
    const allBookmakers = new Set();
    filteredHistory.forEach(entry => {
      if (entry.books && typeof entry.books === 'object') {
        Object.keys(entry.books).forEach(book => {
          if (entry.books[book]) {
            // Check for over/under markets, yes/no markets, or direct number values
            if (entry.books[book].over !== undefined ||
                entry.books[book].yes !== undefined ||
                typeof entry.books[book] === 'number') {
              allBookmakers.add(book);
            }
          }
        });
      }
    });

    // Include every book; Settings → Graphs only sets the initial hidden flag below.
    const bookmakers = Array.from(allBookmakers);
    
    // Return null if no bookmakers found
    if (bookmakers.length === 0) return null;
    
    // Group entries by timestamp to handle multiple markets (main + alt) at same time
    // Normalize timestamps to nearest 5 minutes to combine entries that are close in time
    const groupedByTimestamp = {};
    filteredHistory.forEach(entry => {
      if (!entry.timestamp) return;
      const date = new Date(entry.timestamp);
      // Round to nearest 5 minutes
      const minutes = date.getMinutes();
      const roundedMinutes = Math.round(minutes / 5) * 5;
      date.setMinutes(roundedMinutes);
      date.setSeconds(0);
      date.setMilliseconds(0);
      const normalizedTimestamp = date.toISOString();

      if (!groupedByTimestamp[normalizedTimestamp]) {
        groupedByTimestamp[normalizedTimestamp] = [];
      }
      groupedByTimestamp[normalizedTimestamp].push(entry);
    });

    const timestamps = Object.keys(groupedByTimestamp).sort((a, b) => new Date(a) - new Date(b));

    // Return null if no valid history entries
    if (timestamps.length === 0) return null;

    // Prepare labels (timestamps) - use unique timestamps
    const labels = timestamps.map(timestamp => {
      try {
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } catch (e) {
        return 'Invalid Date';
      }
    });

    // Prepare datasets for each bookmaker - filter out bookmakers with no data
    const datasetsWithLatestValues = bookmakers
      .map(bookmaker => {
        const data = timestamps.map(timestamp => {
          const entries = groupedByTimestamp[timestamp];
          const values = [];

          entries.forEach(entry => {
            if (entry.books && entry.books[bookmaker]) {
              let value;

              // For special markets (binary yes/no), use 'yes' or direct value
              if (selectedTier === null) {
                if (entry.books[bookmaker].yes !== undefined) {
                  value = parseFloat(entry.books[bookmaker].yes);
                } else if (entry.books[bookmaker].over !== undefined) {
                  value = parseFloat(entry.books[bookmaker].over);
                } else if (typeof entry.books[bookmaker] === 'number') {
                  value = parseFloat(entry.books[bookmaker]);
                }
              } else {
                // For over/under markets, use useOver to determine which odds to show
                if (useOver) {
                  if (entry.books[bookmaker].over !== undefined) {
                    value = parseFloat(entry.books[bookmaker].over);
                  } else if (typeof entry.books[bookmaker] === 'number') {
                    value = parseFloat(entry.books[bookmaker]);
                  }
                } else {
                  // Show under odds
                  if (entry.books[bookmaker].under !== undefined) {
                    value = parseFloat(entry.books[bookmaker].under);
                  }
                }
              }

              if (value !== undefined && !isNaN(value)) {
                values.push(value);
              }
            }
          });

          // If multiple values exist (main + alt), use the best (highest) one
          if (values.length > 0) {
            return Math.max(...values);
          }
          return null;
        });
        
        // Check if this bookmaker has any non-null data
        const hasData = data.some(value => value !== null);
        if (!hasData) return null;
        
        // Get the latest (current) value for sorting
        let latestValue = null;
        for (let i = data.length - 1; i >= 0; i--) {
          if (data[i] !== null) {
            latestValue = data[i];
            break;
          }
        }
        
        // Calculate point radius - show dot only when value changes
        const pointRadius = data.map((value, index) => {
          if (value === null) return 0;
          if (index === 0) return 3; // Always show first point
          if (index === data.length - 1) return 3; // Always show last point
          
          // Show dot if value is different from previous non-null value
          let prevValue = null;
          for (let i = index - 1; i >= 0; i--) {
            if (data[i] !== null) {
              prevValue = data[i];
              break;
            }
          }
          
          return (prevValue !== null && value !== prevValue) ? 3 : 0;
        });

        // Apply forward alias for display (e.g., williamhill_us -> caesars)
        const displayName = BOOKMAKER_ALIASES[bookmaker] || bookmaker;

        return {
          label: displayName.charAt(0).toUpperCase() + displayName.slice(1),
          data: data,
          borderColor: bookmakerColors[bookmaker] || '#999999',
          backgroundColor: bookmakerColors[bookmaker] || '#999999',
          tension: 0.1,
          pointRadius: pointRadius,
          pointHoverRadius: 6,
          spanGaps: true, // Connect points even if there are null values
          hidden: isBookEnabledForGraphs ? !isBookEnabledForGraphs(bookmaker) : false,
          latestValue: latestValue // Store for sorting
        };
      })
      .filter(dataset => dataset !== null); // Remove null datasets
    
    // Sort datasets by latest value (highest to lowest)
    const datasets = datasetsWithLatestValues
      .sort((a, b) => {
        const aValue = a.latestValue || -Infinity;
        const bValue = b.latestValue || -Infinity;
        return bValue - aValue; // Sort descending (highest first)
      })
      .map(dataset => {
        // Remove the latestValue property as it's not needed for Chart.js
        const { latestValue, ...chartDataset } = dataset;
        return chartDataset;
      });

    // Final validation
    if (datasets.length === 0 || labels.length === 0) {
      return null;
    }
    
    const chartData = {
      labels,
      datasets
    };
    
    return chartData;
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    backgroundColor: 'white',
    layout: {
      padding: {
        bottom: 20
      }
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          fontSize: 12
        }
      },
      title: {
        display: true,
        text: `Odds Movement - ${getMarketAbbreviation((showChart && oddsHistoryData) ? oddsHistoryData.marketSearch : (oddsData?.marketSearch || market))}${selectedTier !== null ? ` (${chartIsOver ? 'Over' : 'Under'} ${selectedTier})` : ''}`,
        font: {
          size: 16
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            if (value === null) return null;
            return `${context.dataset.label}: ${value > 0 ? '+' : ''}${value}`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Time'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Odds'
        },
        ticks: {
          callback: function(value) {
            return value > 0 ? `+${value}` : value;
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  const chartData = useMemo(() => {
    if (!show) return null; // Don't prepare data when modal is hidden
    return prepareChartData();
  }, [show, oddsData, oddsHistoryData, selectedTier, showChart, chartIsOver]);

  // Custom plugin to draw book logos above last data points
  const bookLogoPlugin = {
    id: 'bookLogos',
    afterDraw: (chart) => {
      if (!chartData || !getBookLogoUrl) return;
      
      const ctx = chart.ctx;
      const chartArea = chart.chartArea;
      
      // Get the last data point for each dataset
      const lastDataPoints = [];
      
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        if (!meta.hidden && dataset.data.length > 0) {
          // Find the last non-null data point
          for (let i = dataset.data.length - 1; i >= 0; i--) {
            if (dataset.data[i] !== null) {
              const point = meta.data[i];
              if (point && point.x !== undefined && point.y !== undefined) {
                lastDataPoints.push({
                  bookmaker: dataset.label.toLowerCase(),
                  value: dataset.data[i],
                  x: point.x,
                  y: point.y
                });
              }
              break;
            }
          }
        }
      });
      
      if (lastDataPoints.length === 0) return;
      
      // Group by the same price (best book scenario)
      const priceGroups = {};
      lastDataPoints.forEach(point => {
        const price = point.value;
        if (!priceGroups[price]) {
          priceGroups[price] = [];
        }
        priceGroups[price].push(point);
      });
      
      // Draw logos for each price group
      Object.values(priceGroups).forEach(group => {
        if (group.length === 0) return;
        
        // Use the position of the first point in the group
        const baseX = group[0].x;
        const baseY = group[0].y;
        
        // Calculate positions for multiple logos
        const logoSize = 20;
        const spacing = 4;
        const totalWidth = (group.length * logoSize) + ((group.length - 1) * spacing);
        const startX = baseX - (totalWidth / 2);
        
        group.forEach((point, index) => {
          const logoX = startX + (index * (logoSize + spacing));
          const logoY = baseY - logoSize - 10; // 10px above the point
          
          // Only draw if within chart area
          if (logoX >= chartArea.left && 
              logoX + logoSize <= chartArea.right && 
              logoY >= chartArea.top) {
            
            // Create image element
            const img = new Image();
            img.onload = () => {
              ctx.save();
              ctx.drawImage(img, logoX, logoY, logoSize, logoSize);
              ctx.restore();
            };
            img.src = getBookLogoUrl(point.bookmaker);
          }
        });
      });
    }
  };

  // Mobile: Use fullscreen Bootstrap Modal (matches NBA modal pattern)
  if (isMobile) {
    return (
      <Modal
        show={show}
        onHide={onHide}
        fullscreen="sm-down"
        centered
        className="player-modal"
        backdropClassName="player-modal-backdrop"
      >
        <ModalBrandingBar />
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="d-flex align-items-center w-100">
            <div className="d-flex align-items-center">
              {/* Player Headshot */}
              {playerId && (
                <img
                  src={`https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:silo:current.png/r_max/w_180,q_auto:best/v1/people/${String(playerId).replace('mlb_', '')}/headshot/silo/current`}
                  alt={playerName || oddsData?.playerName}
                  style={{
                    width: '60px',
                    height: '60px',
                    marginRight: '12px',
                    borderRadius: '50%',
                    border: '3px solid white',
                    backgroundColor: 'white'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}

              {/* Team Logo and Player Info */}
              <div className="d-flex align-items-center">
                {oddsData?.team && (
                  <img
                    src={getTeamLogoUrl(oddsData.team)}
                    alt={oddsData.team}
                    style={{ width: '24px', height: '24px', marginRight: '8px' }}
                  />
                )}
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                    {playerName || oddsData?.playerName || mlbPlayerData?.playerName}
                    {sport === 'mlb' && playerHandInfo && (
                      <span style={{ fontSize: '11px', color: '#adb5bd', marginLeft: '6px' }}>
                        {playerHandInfo.isPitcher
                          ? `Throws ${playerHandInfo.pitchHand === 'R' ? 'Right' : playerHandInfo.pitchHand === 'L' ? 'Left' : playerHandInfo.pitchHand}`
                          : `Bats ${playerHandInfo.batSide === 'R' ? 'Right' : playerHandInfo.batSide === 'L' ? 'Left' : playerHandInfo.batSide === 'S' ? 'Switch' : playerHandInfo.batSide}`}
                      </span>
                    )}
                    {(playerHandInfo?.battingOrder || mlbPlayerData?.battingOrder) && (
                      <span style={{ fontSize: '12px', color: '#ffc107', marginLeft: '8px' }}>
                        Batting {playerHandInfo?.battingOrder || mlbPlayerData?.battingOrder}th
                      </span>
                    )}
                  </div>
                  <small className="opacity-75" style={{ fontSize: '12px' }}>
                    {sport === 'mlb' ? `${awayTeam || 'TBD'} @ ${homeTeam || 'TBD'}` : getMarketAbbreviation(oddsData?.marketSearch || market)}
                  </small>
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: '85vh', overflowY: 'auto' }}>
          {sport === 'mlb' && !showChart ? (
            // MLB Player Odds Table - uses same component as desktop PlayerPanel
            <PlayerOddsTable
              gameId={gameId}
              game={game}
              playerId={playerId}
              playerName={playerName}
              awayTeam={awayTeam}
              homeTeam={homeTeam}
              market={market}
              sport={sport}
              opposingPitcher={opposingPitcher}
              onChartClick={(marketKey, chartPoint, isOver) => {
                fetchOddsHistory(marketKey, chartPoint, isOver);
              }}
              onPlayerInfo={handlePlayerInfo}
            />
          ) : sport === 'mlb' && showChart ? (
            // MLB Odds Movement Chart
            <div className="p-2">
              <div className="mb-3">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleBackToTable}
                >
                  ← Back to Odds Table
                </Button>
              </div>

              {loadingChart && (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading chart...</span>
                  </Spinner>
                </div>
              )}

              {!loadingChart && chartData && chartData.datasets && chartData.datasets.length > 0 && chartData.labels && chartData.labels.length > 0 ? (
                <div style={{
                  height: '70vh',
                  position: 'relative',
                  padding: '10px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}>
                  <Line
                    key={`chart-${gameId}-${playerId}-${chartMarket}-${chartPoint}`}
                    data={chartData}
                    options={{
                      ...chartOptions,
                      animation: { duration: 0 },
                      plugins: {
                        ...chartOptions.plugins,
                        title: { ...chartOptions.plugins.title, font: { size: 14 } },
                        legend: { position: 'bottom', labels: { boxWidth: 10, fontSize: 11 } }
                      }
                    }}
                    plugins={[]}
                  />
                </div>
              ) : !loadingChart && (
                <div className="alert alert-warning" role="alert">
                  No odds movement data available for this market.
                </div>
              )}
            </div>
          ) : (
            // Other sports - original loading/error/chart flow
            <>
              {loading && (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                  <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              )}

              {error && (
                <div className="alert alert-danger" role="alert">
                  <h5>Unable to load odds movement</h5>
                  <p>{error}</p>
                  <Button variant="outline-primary" size="sm" onClick={fetchOddsMovement}>
                    Try Again
                  </Button>
                </div>
              )}

              {!loading && !error && oddsData && (
                <>
                  {getAvailableTiers().length > 1 && (
                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-center flex-wrap" style={{ gap: '8px' }}>
                        <label className="form-label mb-0 me-2">
                          <strong>Select Tier:</strong>
                        </label>
                        <div className="btn-group btn-group-sm" role="group">
                          {getAvailableTiers().map(tier => (
                            <React.Fragment key={tier}>
                              <input
                                type="radio"
                                className="btn-check"
                                name="tier-selection"
                                id={`tier-${tier}`}
                                checked={selectedTier === tier}
                                onChange={() => setSelectedTier(tier)}
                                autoComplete="off"
                              />
                              <label
                                className={`btn ${selectedTier === tier ? 'btn-primary' : 'btn-outline-primary'}`}
                                htmlFor={`tier-${tier}`}
                              >
                                Over {tier}
                              </label>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {chartData && chartData.datasets && chartData.datasets.length > 0 && chartData.labels && chartData.labels.length > 0 ? (
                    <div style={{
                      height: '380px',
                      position: 'relative',
                      padding: '10px',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}>
                      <Line
                        key={`chart-${gameId}-${playerId}-${market}`}
                        data={chartData}
                        options={{
                          ...chartOptions,
                          animation: { duration: 0 },
                          plugins: {
                            ...chartOptions.plugins,
                            title: { ...chartOptions.plugins.title, font: { size: 14 } },
                            legend: { position: 'bottom', labels: { boxWidth: 10, fontSize: 11 } }
                          }
                        }}
                        plugins={[]}
                      />
                    </div>
                  ) : (
                    <div className="alert alert-warning" role="alert">
                      No odds movement data available for this player/market combination.
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>

        {/* Edge % Tooltip */}
        {tooltipData && (
          <div
            style={{
              position: 'fixed',
              left: `${tooltipData.x + 10}px`,
              top: `${tooltipData.y + 10}px`,
              backgroundColor: '#1f2937',
              color: 'white',
              padding: '12px 16px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              zIndex: 9999,
              fontSize: '0.85rem',
              minWidth: '200px',
              pointerEvents: 'none'
            }}
          >
            <div style={{ marginBottom: '8px', fontWeight: 'bold', borderBottom: '1px solid #4b5563', paddingBottom: '6px' }}>
              {tooltipData.book.charAt(0).toUpperCase() + tooltipData.book.slice(1)}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span>Odds:</span>
              <span style={{ fontWeight: 'bold' }}>{tooltipData.odds > 0 ? `+${tooltipData.odds}` : tooltipData.odds}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span>Implied Prob:</span>
              <span style={{ fontWeight: 'bold', color: '#60a5fa' }}>{tooltipData.impliedProb}%</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Edge:</span>
              <span style={{ fontWeight: 'bold', color: parseFloat(tooltipData.edge) > 0 ? '#34d399' : '#f87171' }}>
                {parseFloat(tooltipData.edge) > 0 ? '+' : ''}{tooltipData.edge}%
              </span>
            </div>
          </div>
        )}
      </Modal>
    );
  }
  
  // Desktop: Keep as modal
  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered
      className="player-modal"
      backdropClassName="player-modal-backdrop"
    >
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title className="d-flex align-items-center w-100">
          <div className="d-flex align-items-center justify-content-between w-100">
            <div className="d-flex align-items-center">
              {/* Player Headshot */}
              {playerId && (
                <img
                  src={`https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:silo:current.png/r_max/w_180,q_auto:best/v1/people/${String(playerId).replace('mlb_', '')}/headshot/silo/current`}
                  alt={playerName || oddsData?.playerName}
                  style={{ 
                    width: '60px', 
                    height: '60px', 
                    marginRight: '16px',
                    borderRadius: '50%',
                    border: '3px solid white',
                    backgroundColor: 'white'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              
              {/* Team Logo and Player Info */}
              <div className="d-flex align-items-center">
                {oddsData?.team && (
                  <img
                    src={getTeamLogoUrl(oddsData.team)}
                    alt={oddsData.team}
                    style={{ width: '32px', height: '32px', marginRight: '12px' }}
                  />
                )}
                <div>
                  <div>
                    {playerName || oddsData?.playerName || mlbPlayerData?.playerName}
                    {sport === 'mlb' && playerHandInfo && (
                      <span style={{ fontSize: '12px', color: '#adb5bd', marginLeft: '8px' }}>
                        {playerHandInfo.isPitcher
                          ? `Throws ${playerHandInfo.pitchHand === 'R' ? 'Right' : playerHandInfo.pitchHand === 'L' ? 'Left' : playerHandInfo.pitchHand}`
                          : `Bats ${playerHandInfo.batSide === 'R' ? 'Right' : playerHandInfo.batSide === 'L' ? 'Left' : playerHandInfo.batSide === 'S' ? 'Switch' : playerHandInfo.batSide}`}
                      </span>
                    )}
                    {mlbPlayerData?.battingOrder && (
                      <span style={{ fontSize: '14px', color: '#ffc107', marginLeft: '12px' }}>
                        Batting {mlbPlayerData.battingOrder}th
                      </span>
                    )}
                  </div>
                  <small className="opacity-75">
                    {sport === 'mlb' ? `Player Odds • ${awayTeam || 'TBD'} @ ${homeTeam || 'TBD'}` : `${getMarketAbbreviation(oddsData?.marketSearch || market)} • ${awayTeam || 'TBD'} @ ${homeTeam || 'TBD'}`}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ minHeight: isMobile ? 'auto' : '500px', maxHeight: isMobile ? '85vh' : 'none', overflowY: 'auto' }}>
        {loading && (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        )}
        
        {error && (
          <div className="alert alert-danger" role="alert">
            <h5>⚠️ Unable to load odds movement</h5>
            <p>{error}</p>
            <Button variant="outline-primary" size="sm" onClick={fetchOddsMovement}>
              🔄 Try Again
            </Button>
          </div>
        )}
        
        {!loading && !error && oddsData && (
          <>
            {sport === 'mlb' && mlbPlayerData && !showChart ? (
              // MLB Player Odds Table Display for Desktop
              <div className="p-3">
                {/* Show Unders Toggle */}
                <div className="d-flex justify-content-center mb-3">
                  <Button
                    variant={showUnders ? 'primary' : 'outline-primary'}
                    size="sm"
                    onClick={() => setShowUnders(!showUnders)}
                  >
                    Show Unders
                  </Button>
                </div>

                {(() => {
                  const processedData = processMLBOddsData();
                  if (!processedData || processedData.markets.length === 0) {
                    return (
                      <div className="alert alert-info" role="alert">
                        No odds available for this player.
                      </div>
                    );
                  }

                  const { markets, books } = processedData;

                  return (
                    <div className="table-responsive">
                      <Table hover bordered>
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '15%', border: 'none' }}></th>
                            {books.map(book => (
                              <th key={book} style={{ textAlign: 'center', padding: '8px', border: 'none' }}>
                                <img
                                  src={getBookLogo(book)}
                                  alt={book}
                                  style={{ height: '32px', width: 'auto', maxWidth: '60px' }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentNode.innerHTML = book.charAt(0).toUpperCase() + book.slice(1).replace(/_/g, ' ');
                                  }}
                                />
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {markets.filter(market => showUnders || !market.displayName.includes(' U')).map(market => {
                            // Extract market key and point from the composite key
                            const marketKeyParts = market.key.split('_');
                            const isUnder = market.key.includes('_under_');
                            let actualMarketKey, point;

                            if (isUnder) {
                              const underIndex = marketKeyParts.indexOf('under');
                              actualMarketKey = marketKeyParts.slice(0, underIndex).join('_');
                              point = parseFloat(marketKeyParts[marketKeyParts.length - 1]);
                            } else {
                              point = parseFloat(marketKeyParts[marketKeyParts.length - 1]);
                              actualMarketKey = marketKeyParts.slice(0, -1).join('_');
                            }

                            return (
                            <tr key={market.key}>
                              <td style={{ fontWeight: 'bold', fontSize: '1rem', padding: '8px', lineHeight: '1.3' }}>
                                <div>
                                  {market.displayName.includes(' O') ? market.displayName.split(' O')[0] : market.displayName.split(' U')[0]}
                                </div>
                                <div
                                  style={{
                                    fontSize: '0.7rem',
                                    color: '#007bff',
                                    fontWeight: 'normal',
                                    cursor: 'pointer'
                                  }}
                                  onClick={() => fetchOddsHistory(actualMarketKey, point, !isUnder)}
                                >
                                  {market.displayName.includes(' O') && market.displayName.split(' O')[1] && `over ${market.displayName.split(' O')[1]}`}
                                  {market.displayName.includes(' U') && market.displayName.split(' U')[1] && `under ${market.displayName.split(' U')[1]}`}
                                </div>
                              </td>
                              {books.map(book => {
                                const odds = market.books[book];
                                const tierStyle = getTierColorForMarket(market, book, odds);
                                const isAvailable = market.booksAvailable?.[book] !== false;

                                return (
                                  <td
                                    key={book}
                                    style={{
                                      textAlign: 'center',
                                      padding: '8px',
                                      ...tierStyle,
                                      borderRadius: '4px',
                                      cursor: odds ? 'pointer' : 'default',
                                      opacity: !isAvailable ? 0.25 : undefined
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (odds) {
                                        handleOddsClick(e, odds, book, point, market.bestBook, market.bestBookDiff);
                                      }
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!isMobile && odds) {
                                        handleOddsClick(e, odds, book, point, market.bestBook, market.bestBookDiff);
                                      }
                                    }}
                                    onMouseLeave={() => {
                                      if (!isMobile) {
                                        setTooltipData(null);
                                      }
                                    }}
                                  >
                                    {odds ? (
                                      <span style={{
                                        fontWeight: 'bold',
                                        fontSize: '1.1rem'
                                      }}>
                                        {odds > 0 ? '+' : ''}{odds}
                                      </span>
                                    ) : (
                                      <span style={{ color: '#6c757d' }}>-</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  );
                })()}

                {/* Helper text for chart feature */}
                <div style={{
                  textAlign: 'center',
                  marginTop: '16px',
                  fontSize: '0.9rem',
                  color: '#6c757d',
                  fontStyle: 'italic'
                }}>
                  📊 Click a point value or market label to view odds movement
                </div>
              </div>
            ) : sport === 'mlb' && showChart ? (
              // MLB Odds Movement Chart for Desktop
              <div className="p-3">
                {/* Back Button */}
                <div className="mb-3">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleBackToTable}
                  >
                    ← Back to Odds Table
                  </Button>
                </div>

                {loadingChart && (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                    <Spinner animation="border" role="status" variant="primary">
                      <span className="visually-hidden">Loading chart...</span>
                    </Spinner>
                  </div>
                )}

                {!loadingChart && chartData && chartData.datasets && chartData.datasets.length > 0 && chartData.labels && chartData.labels.length > 0 ? (
                  <div style={{
                    height: '400px',
                    position: 'relative',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '10px',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    <Line
                      key={`chart-${gameId}-${playerId}-${chartMarket}-${chartPoint}`}
                      data={chartData}
                      options={{
                        ...chartOptions,
                        animation: {
                          duration: 0
                        }
                      }}
                      plugins={[]}
                    />
                  </div>
                ) : !loadingChart && (
                  <div className="alert alert-warning" role="alert">
                    No odds movement data available for this market.
                  </div>
                )}
              </div>
            ) : (
              // Original odds movement chart for other sports
              <>
                {/* Tier Selection */}
                {getAvailableTiers().length > 1 && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-center flex-wrap" style={{ gap: '8px' }}>
                      <label className="form-label mb-0 me-2">
                        <strong>Select Tier:</strong>
                      </label>
                      <div className="btn-group" role="group">
                        {getAvailableTiers().map(tier => (
                          <React.Fragment key={tier}>
                            <input
                              type="radio"
                              className="btn-check"
                              name="tier-selection"
                              id={`tier-${tier}`}
                              checked={selectedTier === tier}
                              onChange={() => setSelectedTier(tier)}
                              autoComplete="off"
                            />
                            <label
                              className={`btn ${selectedTier === tier ? 'btn-primary' : 'btn-outline-primary'} btn-sm`}
                              htmlFor={`tier-${tier}`}
                            >
                              Over {tier}
                            </label>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Chart */}
                {chartData && chartData.datasets && chartData.datasets.length > 0 && chartData.labels && chartData.labels.length > 0 ? (
                  <div style={{
                    height: '400px',
                    position: 'relative',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '10px',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}>
                    <Line
                      key={`chart-${gameId}-${playerId}-${market}`}
                      data={chartData}
                      options={{
                        ...chartOptions,
                        animation: {
                          duration: 0 // Disable animations to prevent issues
                        }
                      }}
                      plugins={[]}
                    />
                  </div>
                ) : (
                  <div className="alert alert-warning" role="alert">
                    No odds movement data available for this player/market combination.
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>

      {/* Edge % Tooltip */}
      {tooltipData && (
        <div
          style={{
            position: 'fixed',
            left: `${tooltipData.x + 10}px`,
            top: `${tooltipData.y + 10}px`,
            backgroundColor: '#1f2937',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 9999,
            fontSize: '0.85rem',
            minWidth: '200px',
            pointerEvents: 'none'
          }}
        >
          <div style={{ marginBottom: '8px', fontWeight: 'bold', borderBottom: '1px solid #4b5563', paddingBottom: '6px' }}>
            {tooltipData.book.charAt(0).toUpperCase() + tooltipData.book.slice(1)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>Odds:</span>
            <span style={{ fontWeight: 'bold' }}>{tooltipData.odds > 0 ? `+${tooltipData.odds}` : tooltipData.odds}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>Implied Prob:</span>
            <span style={{ fontWeight: 'bold', color: '#60a5fa' }}>{tooltipData.impliedProb}%</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Edge:</span>
            <span style={{ fontWeight: 'bold', color: parseFloat(tooltipData.edge) > 0 ? '#34d399' : '#f87171' }}>
              {parseFloat(tooltipData.edge) > 0 ? '+' : ''}{tooltipData.edge}%
            </span>
          </div>
        </div>
      )}

    </Modal>
  );
};

export default PlayerModal;